import React, { useEffect, useState } from "react";
import Papa from "papaparse";
import { api, fmtError, fmtDateTime, API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UploadSimple, DownloadSimple, CheckCircle, XCircle, ClockCounterClockwise, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

/**
 * Detailed platform report tab.
 * - Reporting role uploads a CSV parsed client-side via PapaParse.
 * - Execution Head + Super Admin can approve / reject.
 * - Execution + Reporting can view.
 */

const CSV_FIELDS = [
    "date", "creative_name", "targeting_websites", "geo",
    "impressions", "clicks", "ctr",
    "first_quartile", "second_quartile", "third_quartile", "completed_views", "completed_ratio",
    "reach", "sessions", "users", "new_users", "bounce_rate", "session_rate", "leads",
];

const HEADER_ALIASES = {
    "date": "date",
    "creative": "creative_name",
    "creative name": "creative_name",
    "targeting": "targeting_websites",
    "targeting/websites": "targeting_websites",
    "targeting websites": "targeting_websites",
    "websites": "targeting_websites",
    "geo": "geo",
    "impressions": "impressions",
    "impression": "impressions",
    "clicks": "clicks",
    "ctr": "ctr",
    "1st quartile": "first_quartile",
    "first quartile": "first_quartile",
    "2nd quartile": "second_quartile",
    "second quartile": "second_quartile",
    "3rd quartile": "third_quartile",
    "third quartile": "third_quartile",
    "completed views": "completed_views",
    "completes": "completed_views",
    "completed ratio": "completed_ratio",
    "completion rate": "completed_ratio",
    "reach": "reach",
    "sessions": "sessions",
    "users": "users",
    "new users": "new_users",
    "bounce rate": "bounce_rate",
    "session rate": "session_rate",
    "leads": "leads",
};


function normalizeRow(raw) {
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
        const key = HEADER_ALIASES[String(k).trim().toLowerCase()];
        if (key) out[key] = v;
    }
    // Numeric coercion where applicable
    for (const k of CSV_FIELDS) {
        if (out[k] === undefined) continue;
        if (["date", "creative_name", "targeting_websites", "geo"].includes(k)) continue;
        const n = parseFloat(String(out[k] ?? "").replace(/[%,\s]/g, ""));
        out[k] = Number.isFinite(n) ? n : 0;
    }
    return out;
}


export default function DetailedReportTab({ campaignId, role }) {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUpload, setShowUpload] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [decisionFor, setDecisionFor] = useState(null); // {report, decision}

    const canUpload = role === "reporting" || role === "super_admin";
    const canDecide = role === "execution_head" || role === "super_admin";
    const canDelete = role === "execution_head" || role === "super_admin";
    const confirm = useConfirm();

    const remove = async (r) => {
        const ok = await confirm({
            title: "Delete detailed report?",
            description: `${r.filename} · ${r.row_count} rows. This cannot be undone.`,
            confirmLabel: "Delete",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/detailed-reports/${r.id}`);
            toast.success("Detailed report deleted");
            load();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/campaigns/${campaignId}/detailed-reports`);
            setReports(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [campaignId]);

    return (
        <div className="space-y-6" data-testid="detailed-report-tab">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="overline">Reporting</div>
                    <h3 className="font-display text-xl">Detailed platform reports</h3>
                    <p className="text-xs text-slate-500 mt-1">
                        Upload a CSV of per-creative daily / period metrics. Rows go to the Execution Head for approval before being marked final.
                    </p>
                </div>
                {canUpload && (
                    <Button
                        onClick={() => setShowUpload(true)}
                        data-testid="detailed-report-upload-open"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2 h-9"
                    >
                        <UploadSimple size={14} weight="bold" /> Upload CSV
                    </Button>
                )}
            </div>

            {loading && <p className="text-xs font-mono text-slate-400">loading…</p>}

            {!loading && reports.length === 0 && (
                <div className="border border-dashed border-slate-300 p-12 text-center" data-testid="detailed-report-empty">
                    <p className="text-sm text-slate-500">No detailed reports uploaded yet.</p>
                </div>
            )}

            {!loading && reports.length > 0 && (
                <div className="border border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="w-8"></th>
                                <th className="text-left px-3 py-2 overline">Filename</th>
                                <th className="text-left px-3 py-2 overline">Rows</th>
                                <th className="text-left px-3 py-2 overline">Uploaded by</th>
                                <th className="text-left px-3 py-2 overline">Uploaded at</th>
                                <th className="text-left px-3 py-2 overline">Status</th>
                                <th className="text-right px-3 py-2 overline">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {reports.map((r) => {
                                const isOpen = expanded === r.id;
                                return (
                                    <React.Fragment key={r.id}>
                                        <tr
                                            className={`border-t border-slate-200 cursor-pointer hover:bg-slate-50 ${isOpen ? "bg-slate-50" : ""}`}
                                            onClick={() => setExpanded(isOpen ? null : r.id)}
                                            data-testid={`detailed-report-row-${r.id}`}
                                        >
                                            <td className="px-2 py-2 text-slate-400 font-mono">{isOpen ? "−" : "+"}</td>
                                            <td className="px-3 py-2 font-mono text-xs truncate max-w-[280px]">{r.filename}</td>
                                            <td className="px-3 py-2 font-mono text-xs">{r.row_count}</td>
                                            <td className="px-3 py-2 text-xs">{r.created_by_name}</td>
                                            <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{fmtDateTime(r.created_at)}</td>
                                            <td className="px-3 py-2">
                                                <StatusBadge status={r.status} />
                                            </td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                <a
                                                    href={`${API}/detailed-reports/${r.id}/csv`}
                                                    data-testid={`detailed-report-csv-${r.id}`}
                                                    className="p-1.5 inline-flex text-slate-500 hover:text-slate-900"
                                                    title="Download CSV"
                                                >
                                                    <DownloadSimple size={14} />
                                                </a>
                                                {canDecide && r.status === "submitted" && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => setDecisionFor({ report: r, decision: "approved" })}
                                                            className="p-1.5 text-slate-500 hover:text-green-700"
                                                            title="Approve"
                                                            data-testid={`detailed-report-approve-${r.id}`}
                                                        >
                                                            <CheckCircle size={14} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setDecisionFor({ report: r, decision: "rejected" })}
                                                            className="p-1.5 text-slate-500 hover:text-red-700"
                                                            title="Reject"
                                                            data-testid={`detailed-report-reject-${r.id}`}
                                                        >
                                                            <XCircle size={14} />
                                                        </button>
                                                    </>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        type="button"
                                                        onClick={() => remove(r)}
                                                        className="p-1.5 text-slate-500 hover:text-red-700"
                                                        title="Delete"
                                                        data-testid={`detailed-report-delete-${r.id}`}
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="bg-slate-50" data-testid={`detailed-report-expanded-${r.id}`}>
                                                <td></td>
                                                <td colSpan={6} className="px-3 pb-4 pt-1">
                                                    <ReportRowsPreview rows={r.rows} />
                                                    {r.status === "rejected" && r.rejection_reason && (
                                                        <p className="mt-3 text-xs text-red-700 font-mono">Rejection: {r.rejection_reason}</p>
                                                    )}
                                                    {r.status !== "submitted" && r.decided_by_name && (
                                                        <p className="mt-2 text-[11px] text-slate-500 font-mono">
                                                            {r.status} by {r.decided_by_name} · {fmtDateTime(r.decided_at)}
                                                        </p>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {showUpload && (
                <UploadModal
                    campaignId={campaignId}
                    onClose={() => setShowUpload(false)}
                    onUploaded={() => { setShowUpload(false); load(); }}
                />
            )}

            {decisionFor && (
                <DecisionModal
                    report={decisionFor.report}
                    decision={decisionFor.decision}
                    onClose={() => setDecisionFor(null)}
                    onDecided={() => { setDecisionFor(null); load(); }}
                />
            )}
        </div>
    );
}


function StatusBadge({ status }) {
    const cls = status === "approved"
        ? "border-green-300 text-green-700 bg-green-50"
        : status === "rejected"
            ? "border-red-300 text-red-700 bg-red-50"
            : "border-amber-300 text-amber-700 bg-amber-50";
    return (
        <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border ${cls}`}>
            {status === "submitted" && <ClockCounterClockwise size={10} weight="bold" />}
            {status}
        </span>
    );
}


function ReportRowsPreview({ rows }) {
    const [expanded, setExpanded] = useState(false);
    const preview = expanded ? rows : rows.slice(0, 5);
    return (
        <div className="border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-xs">
                <thead className="bg-slate-100">
                    <tr>
                        {CSV_FIELDS.map((f) => (
                            <th key={f} className="text-left px-2 py-1.5 font-mono uppercase text-[9px] tracking-wider text-slate-500 whitespace-nowrap">
                                {f.replace(/_/g, " ")}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {preview.map((row, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                            {CSV_FIELDS.map((f) => (
                                <td key={f} className="px-2 py-1 font-mono whitespace-nowrap">{row[f] ?? "—"}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {rows.length > 5 && (
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="w-full py-2 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-slate-900 border-t border-slate-200"
                    data-testid="detailed-report-toggle-rows"
                >
                    {expanded ? "Show first 5" : `Show all ${rows.length} rows`}
                </button>
            )}
        </div>
    );
}


function UploadModal({ campaignId, onClose, onUploaded }) {
    const [file, setFile] = useState(null);
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);

    const downloadSample = () => {
        const today = new Date().toISOString().slice(0, 10);
        const sample = [
            {
                date: today, creative_name: "Banner_300x250_v1", targeting_websites: "cricinfo.com, espncricinfo.com",
                geo: "IN-MH", impressions: 125430, clicks: 842, ctr: 0.0067,
                first_quartile: 118500, second_quartile: 105200, third_quartile: 92400,
                completed_views: 78900, completed_ratio: 0.629, reach: 68200,
                sessions: 512, users: 468, new_users: 321, bounce_rate: 0.42, session_rate: 1.09, leads: 12,
            },
            {
                date: today, creative_name: "Video_16x9_v2", targeting_websites: "youtube.com",
                geo: "IN-DL", impressions: 89210, clicks: 511, ctr: 0.0057,
                first_quartile: 82100, second_quartile: 71300, third_quartile: 60400,
                completed_views: 49800, completed_ratio: 0.558, reach: 45900,
                sessions: 298, users: 271, new_users: 187, bounce_rate: 0.51, session_rate: 1.10, leads: 8,
            },
        ];
        const esc = (v) => {
            const s = v === null || v === undefined ? "" : String(v);
            return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
        };
        const csv = [CSV_FIELDS.join(","), ...sample.map((r) => CSV_FIELDS.map((k) => esc(r[k])).join(","))].join("\n") + "\n";
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "detailed-report-sample.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success("Sample CSV downloaded");
    };

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        Papa.parse(f, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const parsed = (results.data || []).map(normalizeRow).filter((r) => Object.keys(r).length > 0);
                if (parsed.length === 0) {
                    toast.error("CSV parsed but no valid rows found. Check the header names.");
                    setFile(null);
                    setRows([]);
                    return;
                }
                setRows(parsed);
            },
            error: (err) => {
                toast.error(`CSV parse error: ${err.message}`);
                setFile(null);
            },
        });
    };

    const submit = async () => {
        if (!file || rows.length === 0) {
            toast.error("Pick a CSV file first.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/campaigns/${campaignId}/detailed-reports`, {
                filename: file.name,
                rows,
            });
            toast.success(`Uploaded ${rows.length} rows — awaiting approval.`);
            onUploaded?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="detailed-report-upload-modal">
            <div className="bg-white border border-slate-200 max-w-3xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Upload</div>
                        <h3 className="font-display text-lg">Detailed report CSV</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label className="overline">CSV file</label>
                            <button
                                type="button"
                                onClick={downloadSample}
                                data-testid="detailed-report-download-sample"
                                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-600 hover:text-slate-900 border border-slate-300 px-2 py-1"
                            >
                                <DownloadSimple size={12} weight="bold" /> Download sample
                            </button>
                        </div>
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            onChange={handleFile}
                            data-testid="detailed-report-file-input"
                            className="block w-full text-sm border border-slate-300 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-slate-900 file:text-white file:font-mono file:text-xs file:uppercase file:tracking-wider"
                        />
                        <p className="text-[10px] text-slate-500 font-mono mt-2">
                            Expected columns: date, creative name, targeting/websites, geo, impressions, clicks, ctr,
                            1st/2nd/3rd quartile, completed views, completed ratio, reach, sessions, users, new users,
                            bounce rate, session rate, leads. Header row required. Common aliases are accepted.
                        </p>
                    </div>
                    {rows.length > 0 && (
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="overline">Preview — {rows.length} rows detected</span>
                            </div>
                            <ReportRowsPreview rows={rows} />
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={busy || rows.length === 0}
                        data-testid="detailed-report-submit"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6"
                    >
                        {busy ? "Uploading…" : `Submit ${rows.length || ""} rows`.trim()}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function DecisionModal({ report, decision, onClose, onDecided }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (decision === "rejected" && !reason.trim()) {
            toast.error("Rejection reason is required.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/detailed-reports/${report.id}/decision`, {
                decision,
                reason: reason.trim() || null,
            });
            toast.success(`Report ${decision}.`);
            onDecided?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="detailed-report-decision-modal">
            <div className="bg-white border border-slate-200 max-w-md w-full mt-16">
                <div className="px-6 py-4 border-b border-slate-200">
                    <div className="overline">Approval</div>
                    <h3 className="font-display text-lg">{decision === "approved" ? "Approve" : "Reject"} detailed report</h3>
                </div>
                <div className="p-6 space-y-3">
                    <p className="text-sm text-slate-700 font-mono">
                        {report.filename} · {report.row_count} rows
                    </p>
                    {decision === "rejected" && (
                        <div>
                            <label className="overline block mb-1.5">Rejection reason</label>
                            <Textarea
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                data-testid="detailed-report-decision-reason"
                                className="rounded-none border-slate-300"
                                placeholder="Why is this being rejected?"
                            />
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        data-testid="detailed-report-decision-submit"
                        className={`rounded-none text-white px-6 ${decision === "approved" ? "bg-green-700 hover:bg-green-800" : "bg-red-700 hover:bg-red-800"}`}
                    >
                        {busy ? "…" : (decision === "approved" ? "Approve" : "Reject")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
